import React, { useMemo, useState } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTranslation } from "react-i18next";
import { IPaymentCancelCardProps } from "./types";
import CancellationPolicySheet from "./CancellationPolicySheet";
import { useCancellationFeeHandler } from "./handler/useCancellationFeeHandler";
import { formatCurrency } from "../../utils/util";

const PaymentCancelCard: React.FC<IPaymentCancelCardProps> = ({
  cardType,
  maskedPin,
  cancellationFee,
  total,
  status = "active",
  onCancel,
  cancelText,
  txId,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showPolicySheet, setShowPolicySheet] = useState(false);
  const { t } = useTranslation("webReceipt");
  const [loading, setLoading] = useState(false);
  const { getCancellationFee } = useCancellationFeeHandler();
  const [currentCancellationFee, setCurrentCancellationFee] =
    useState<number>(0);

  const handleCancelClick = async () => {
    if (txId) {
      setLoading(true);
      try {
        const response = await getCancellationFee(txId);
        setCurrentCancellationFee(response.cancellationFee);
        console.log("Cancellation fee:", response.cancellationFee);
      } catch (error) {
        console.error("Failed to get cancellation fee:", error);
        setCurrentCancellationFee(cancellationFee);
      } finally {
        setLoading(false);
      }
    }
    setShowModal(true);
  };
  const handleCloseModal = () => setShowModal(false);

  let displayMaskedPin = "-";
  if (cardType !== "OCTOPUS") {
    displayMaskedPin =
      maskedPin && maskedPin.startsWith("undefined") ? "-" : maskedPin || "-";
  }

  const refundAmount = useMemo(() => {
    return total - cancellationFee;
  }, [total, cancellationFee]);

  return (
    <div className="flex flex-col gap-3 mx-5 mt-2 mb-2">
      <div className="flex flex-col bg-white rounded-lg p-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            {(cardType === "VISA" ||
              cardType === "MASTER" ||
              cardType === "GOOGLE_PAY" ||
              cardType === "APPLE_PAY" ||
              cardType === "MASTERCARD" ||
              cardType === "UNIONPAY" ||
              cardType === "ALIPAY" ||
              cardType === "OCTOPUS" ||
              cardType === "WECHAT") && (
              <img
                className="w-6 h-6 object-contain payment-logo"
                src={`/icons8-${cardType.toLowerCase()}.svg`}
                alt={cardType}
              />
            )}
            <span className="text-base font-bold text-mineshaft-900">
              {displayMaskedPin}
            </span>
          </div>
          <div className="text-base font-bold text-mineshaft-900">
            {status === "cancelled" ? `$${cancellationFee}` : `$${total}`}
          </div>
        </div>
        {status === "cancelled" && (
          <>
            {refundAmount > 0 && (
              <div className="flex flex-row justify-between w-full mt-1">
                <span className="text-base font-semi-bold text-brand-green">
                  {t("tripCancelledRefund")}
                </span>
                <span className="text-base font-semi-bold text-brand-green">
                  {formatCurrency(refundAmount)}
                </span>
              </div>
            )}
            <div className="flex flex-row justify-between w-full mt-1">
              <span className="text-base font-semi-bold text-mineshaft-700">
                {t("tripCancelledCancellationFee")}
              </span>
              <span className="text-base font-semi-bold text-mineshaft-700">
                {`$${cancellationFee}`}
              </span>
            </div>
          </>
        )}
      </div>
      {onCancel &&
        (cancelText ?? t("commonCancelButton")) &&
        status !== "cancelled" && (
          <div className="bg-white rounded-lg overflow-hidden">
            <button
              className="flex items-center gap-2 w-full p-4 text-red-500 text-base font-semibold hover:bg-red-50 transition outline-none border-none"
              onClick={handleCancelClick}
            >
              <CancelIcon sx={{ color: "#ef4444", fontSize: 20 }} />
              {cancelText ?? t("commonCancelButton")}
            </button>

            <div className="border-t border-gray-200 mx-4"></div>

            <div className="p-4 pt-3">
              <span className="text-base text-gray-600">
                {t("common_cancelFeeNote")}
                <button
                  className="text-blue-500 font-medium hover:text-blue-600 transition"
                  onClick={() => setShowPolicySheet(true)}
                >
                  {t("common_cancelFeeNoteHyperlink")}
                </button>
              </span>
            </div>
          </div>
        )}

      <CancellationPolicySheet
        isOpen={showPolicySheet}
        onClose={() => setShowPolicySheet(false)}
      />

      {showModal && (
        <div
          onClick={handleCloseModal}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30"
        >
          <div className="bg-white rounded-2xl p-8 min-w-[350px] max-w-[90vw] flex flex-col gap-6 relative">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              onClick={handleCloseModal}
              aria-label={t("close")}
            >
              <span className="text-2xl font-bold">&times;</span>
            </button>
            <div className="text-2xl font-bold text-gray-900 mb-2">
              {t("cancellModalTitle")}
            </div>
            <div className="text-lg text-gray-700 mb-2">
              {currentCancellationFee === 0
                ? t("cancellModal_freeDescription")
                : t("cancellModal_paidDescription", {
                    fee: currentCancellationFee,
                  })}
            </div>
            <button
              className="w-full bg-blue-600 text-white rounded-full py-3 text-lg font-semibold hover:bg-blue-700 transition"
              onClick={handleCloseModal}
            >
              {t("cancellModalPrimaryButton")}
            </button>
            <button
              className="w-full bg-gray-100 text-red-500 rounded-full py-3 text-lg font-semibold hover:bg-red-100 transition"
              onClick={() => {
                if (onCancel) onCancel();
                setShowModal(false);
              }}
            >
              {t("cancellModal_secondaryButton")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentCancelCard;
